import type {
  CursorPaginatedResponse,
  Service,
  ServiceListParams,
  ServiceVariant,
} from '@/libs/interfaces'

import { MOCK_ORG_ID } from '../fixtures/seed'
import { mockServices } from '../fixtures/services'
import { delay, genID, matchesSearch, paginate } from '../helpers'
import { createEntityStore } from '../store'

const serviceStore = createEntityStore<Service, 'serviceID'>(mockServices, 'serviceID')

const filterServices = (params: ServiceListParams): Service[] =>
  serviceStore
    .getAll()
    .filter((service) => (params.storeID ? service.storeID === params.storeID : true))
    .filter((service) =>
      params.serviceCategoryID ? service.serviceCategoryID === params.serviceCategoryID : true,
    )
    .filter((service) => (params.hidden === undefined ? true : service.hidden === params.hidden))
    .filter((service) => matchesSearch(service, params.search, ['name', 'serviceDescription']))

export const servicesApiMock = {
  create: async (body: Partial<Service>): Promise<Partial<Service>> => {
    const service: Service = {
      serviceID: genID('service'),
      name: '',
      orgID: MOCK_ORG_ID,
      currency: 'SEK',
      serviceStatus: 'Active',
      serviceVariants: [],
      ...body,
    }
    serviceStore.insert(service)
    return delay(service)
  },
  getByID: async (params: { orgID?: string; serviceID?: string }): Promise<Partial<Service>> => {
    const service = serviceStore.getById(params.serviceID ?? '')
    return delay(service ?? {})
  },
  list: async (params: ServiceListParams): Promise<Service[]> => delay(filterServices(params)),
  listCursor: async (params: ServiceListParams): Promise<CursorPaginatedResponse<Service>> =>
    delay(paginate(filterServices(params), params)),
  update: async (payload: Partial<Service>) => {
    const updated = payload.serviceID ? serviceStore.update(payload.serviceID, payload) : undefined
    return delay(updated ?? payload)
  },
  delete: async (payload: Partial<Service>): Promise<Partial<Service>> => {
    const service = serviceStore.getById(payload.serviceID ?? '')
    if (payload.serviceID) serviceStore.remove(payload.serviceID)
    return delay(service ?? {})
  },
  createVariant: async (body: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    const variant: ServiceVariant = {
      serviceVariantID: genID('service-variant'),
      serviceID: body.serviceID,
      name: '',
      currency: 'SEK',
      orgID: MOCK_ORG_ID,
      ...body,
    }
    const service = serviceStore.getById(body.serviceID ?? '')
    if (service) {
      serviceStore.update(service.serviceID ?? '', {
        serviceVariants: [...(service.serviceVariants ?? []), variant],
      })
    }
    return delay(variant)
  },
  deleteVariant: async (payload: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    const service = serviceStore.getById(payload.serviceID ?? '')
    const variant = service?.serviceVariants?.find(
      (item) => item.serviceVariantID === payload.serviceVariantID,
    )
    if (service) {
      serviceStore.update(service.serviceID ?? '', {
        serviceVariants: (service.serviceVariants ?? []).filter(
          (item) => item.serviceVariantID !== payload.serviceVariantID,
        ),
      })
    }
    return delay(variant ?? {})
  },
  updateVariant: async (payload: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    const service = serviceStore.getById(payload.serviceID ?? '')
    let updatedVariant: ServiceVariant | undefined
    if (service) {
      const serviceVariants = (service.serviceVariants ?? []).map((variant) => {
        if (variant.serviceVariantID !== payload.serviceVariantID) return variant
        updatedVariant = { ...variant, ...payload }
        return updatedVariant
      })
      serviceStore.update(service.serviceID ?? '', { serviceVariants })
    }
    return delay(updatedVariant ?? payload)
  },
}
